import { ServiceError } from '../../contracts';
import type { PlatformPaymentService, PlatformSubscriptionService } from '../../platformContracts';
import { dataFor, delay, matches, paginate, pctx, planOf, recordAudit, savePlatform, syncTenantFeatures, platformRoot } from './context';

const notFound = (w: string) => new ServiceError(`${w} not found`, 'NOT_FOUND');

export const platformSubscriptions: PlatformSubscriptionService = {
  async list(q) {
    pctx('platform.billing.manage');
    const rows = platformRoot().subscriptions
      .filter((s) => (!q.status || q.status === 'all' || s.status === q.status) && matches(q.q, s.tenantName, s.planName))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async get(tenantId) {
    pctx('platform.billing.manage');
    const s = platformRoot().subscriptions.find((x) => x.tenantId === tenantId);
    if (!s) throw notFound('Subscription');
    return delay(s);
  },
  async changePlan(tenantId, planId, cycle) {
    const { actor } = pctx('platform.billing.manage');
    const t = dataFor(tenantId);
    const plan = planOf(planId);
    const s = platformRoot().subscriptions.find((x) => x.tenantId === tenantId);
    if (!s) throw notFound('Subscription');
    const fromPlan = s.planName;
    t.planId = planId;
    syncTenantFeatures(tenantId);
    s.planId = planId; s.planName = plan.name; s.cycle = cycle; s.amount = cycle === 'YEARLY' ? plan.priceYearly : plan.priceMonthly; s.status = t.status === 'TRIAL' ? 'TRIALING' : 'ACTIVE';
    savePlatform();
    recordAudit(actor, 'subscription.plan_changed', 'SUBSCRIPTION', s.id, t.name, `Subscription moved from ${fromPlan} to ${plan.name} (${cycle.toLowerCase()})`);
    return delay(s, 500);
  },
  async cancel(tenantId) {
    const { actor } = pctx('platform.billing.manage');
    const t = dataFor(tenantId);
    const s = platformRoot().subscriptions.find((x) => x.tenantId === tenantId);
    if (!s) throw notFound('Subscription');
    if (s.status === 'CANCELLED') throw new ServiceError('This subscription is already cancelled', 'CONFLICT');
    s.cancelAtPeriodEnd = true; s.status = 'CANCELLED';
    savePlatform();
    recordAudit(actor, 'subscription.cancelled', 'SUBSCRIPTION', s.id, t.name, `Subscription cancelled, active until ${new Date(s.currentPeriodEnd).toLocaleDateString('en-IN')}`);
    return delay(s, 400);
  },
  async reactivate(tenantId) {
    const { actor } = pctx('platform.billing.manage');
    const t = dataFor(tenantId);
    const s = platformRoot().subscriptions.find((x) => x.tenantId === tenantId);
    if (!s) throw notFound('Subscription');
    if (s.status !== 'CANCELLED' && s.status !== 'PAST_DUE') throw new ServiceError('This subscription is not cancelled or past due', 'CONFLICT');
    s.cancelAtPeriodEnd = false; s.status = t.status === 'TRIAL' ? 'TRIALING' : 'ACTIVE';
    savePlatform();
    recordAudit(actor, 'subscription.reactivated', 'SUBSCRIPTION', s.id, t.name, 'Subscription reactivated');
    return delay(s, 400);
  },
};

export const platformPayments: PlatformPaymentService = {
  async list(q) {
    pctx('platform.billing.manage');
    const rows = platformRoot().payments
      .filter((p) => (!q.status || q.status === 'all' || p.status === q.status) && (!q.tenantId || p.tenantId === q.tenantId) && matches(q.q, p.tenantName, p.invoiceNumber))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async refund(id, reason) {
    const { actor } = pctx('platform.billing.manage');
    const p = platformRoot().payments.find((x) => x.id === id);
    if (!p) throw notFound('Payment');
    if (p.status !== 'PAID') throw new ServiceError('Only a paid invoice can be refunded', 'CONFLICT');
    if (!reason.trim()) throw new ServiceError('A reason is required to refund an invoice', 'INVALID');
    p.status = 'REFUNDED';
    savePlatform();
    recordAudit(actor, 'payment.refunded', 'PAYMENT', id, p.tenantName, `Refunded ${p.invoiceNumber} — ${reason.trim()}`);
    return delay(p, 500);
  },
};
